import { config } from '../../utils/utils';
import store from '../../store';
import { toastHandler } from '../../utils/toastHandler';

// Types
export interface CompanyWallet {
  totalPurchased: number;
  totalExpiredCredits?: number; // NEW: Total credits from expired packages
  totalUsed: number;
  totalUsedByAgents?: number; // NEW: Total credits used by all agents
  totalUsedByCompany?: number; // NEW: Total credits used by company main account
  totalAllocatedToAgents?: number; // New field from backend
  totalAvailable: number;
  allocatedToAgents: number; // Keep for backward compatibility
  remainingInWallet: number;
}

export interface AgentCreditInfo {
  agentId: string;
  agentName: string;
  activeState?: boolean;
  allocatedCredits: number;
  usedCredits: number;
  remainingCredits: number;
}

export interface CreditPackage {
  id: string;
  packageName: string; // Package name (labelEn)
  totalCredits: number;
  usedCredits: number;
  allocatedToAgents: number; // New field: total credits allocated to all agents from this package
  remainingCredits: number;
  purchasedAt: string;
  expiresAt: string;
  isActive: boolean;
}

export interface CompanyCreditsResponse {
  companyWallet: CompanyWallet;
  agents: AgentCreditInfo[];
  creditPackages: CreditPackage[];
}

export interface PurchaseCreditRequest {
  packageId: string;
  startDate?: string;
}

export interface PurchaseCreditResponse {
  payUrl: string | null;
  paymentId: string;
  creditCount: number;
  status: string;
}

export interface AllocateCreditsRequest {
  agentId: string;
  credits: number;
}

export interface AllocateCreditsResponse {
  success: boolean;
  agentCredits: {
    allocatedCredits: number;
    usedCredits: number;
    remainingCredits: number;
  };
  companyWallet: {
    remainingCredits: number;
  };
}

export interface PublishAdsRequest {
  advertiseIds: string[];
  startDate?: string;
}

export interface PublishAdsResponse {
  success: boolean;
  publishedCount: number;
  remainingCredits: number;
  message: string;
}

export interface PurchaseHistoryItem {
  id: string;
  paymentId: string;
  packageName: string;
  creditCount: number;
  price: number;
  purchasedAt: string;
  expiresAt: string | null;
  usedCredits: number;
  remainingCredits: number;
  isActive: boolean;
  isExpired: boolean;
  paymentStatus: string;
  companyId?: string; // For admin views
  companyName?: string; // For admin views
  companyEmail?: string; // For admin views
}

export interface PurchaseHistoryResponse {
  data: PurchaseHistoryItem[];
  total: number;
  page: number;
  limit: number;
}

export interface AgentUsageHistoryItem {
  id: string;
  agentId: string;
  agentName: string;
  agentEmail: string;
  advertiseId: string;
  advertiseTitle: string;
  advertiseNumber: string;
  creditsUsed: number;
  usedAt: string;
  companyId?: string; // For admin views
  companyName?: string; // For admin views
  companyEmail?: string; // For admin views
}

export interface AgentUsageHistoryResponse {
  data: AgentUsageHistoryItem[];
  total: number;
  page: number;
  limit: number;
  summary: {
    totalCreditsUsed: number;
    byAgent: Array<{
      agentId: string;
      agentName: string;
      creditsUsed: number;
    }>;
  };
}

export interface AllocationHistoryItem {
  id: string;
  agentId: string | null;
  agentName: string;
  actionType: 'allocate' | 'reduce' | 'expiration';
  creditsAmount: number;
  previousAllocated: number | null;
  newAllocated: number | null;
  previousCompanyWallet: number | null;
  newCompanyWallet: number | null;
  performedBy: string | null;
  performedByName: string;
  performedAt: string;
  companyId?: string; // For admin views
  companyName?: string; // For admin views
  companyEmail?: string; // For admin views
}

export interface AllocationHistoryResponse {
  data: AllocationHistoryItem[];
  total: number;
  page: number;
  limit: number;
}

/**
 * Get company credits (wallet and agent allocations)
 */
export const getCompanyCredits = async (): Promise<CompanyCreditsResponse> => {
  try {
    const { accessToken } = store.getState().auth;

    const response = await fetch(`${config.BASE_URL}/company-credits`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to fetch company credits');
    }

    return await response.json();
  } catch (error: any) {
    toastHandler(error);
    throw error;
  }
};

/**
 * Purchase credits
 */
export const purchaseCredits = async (
  data: PurchaseCreditRequest
): Promise<PurchaseCreditResponse> => {
  try {
    const { accessToken } = store.getState().auth;

    const response = await fetch(`${config.BASE_URL}/company-credits/purchase`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to purchase credits');
    }

    return await response.json();
  } catch (error: any) {
    toastHandler(error);
    throw error;
  }
};

/**
 * Allocate credits to an agent
 */
export const allocateCredits = async (
  data: AllocateCreditsRequest
): Promise<AllocateCreditsResponse> => {
  try {
    const { accessToken } = store.getState().auth;

    const response = await fetch(`${config.BASE_URL}/company-credits/allocate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to allocate credits');
    }

    return await response.json();
  } catch (error: any) {
    toastHandler(error);
    throw error;
  }
};

/**
 * Publish ads using credits
 */
export const publishAdsWithCredits = async (
  data: PublishAdsRequest
): Promise<PublishAdsResponse> => {
  try {
    const { accessToken } = store.getState().auth;

    const response = await fetch(`${config.BASE_URL}/company-credits/publish-ads`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to publish ads');
    }

    return await response.json();
  } catch (error: any) {
    toastHandler(error);
    throw error;
  }
};

/**
 * Get purchase history
 */
export const getPurchaseHistory = async (
  options?: { page?: number; limit?: number; status?: string; companyId?: string }
): Promise<PurchaseHistoryResponse> => {
  try {
    const { accessToken } = store.getState().auth;

    let url = `${config.BASE_URL}/company-credits/purchase-history`;
    const params = new URLSearchParams();

    if (options?.page !== undefined) {
      params.append('page', options.page.toString());
    }
    if (options?.limit !== undefined) {
      params.append('limit', options.limit.toString());
    }
    if (options?.status) {
      params.append('status', options.status);
    }
    if (options?.companyId) {
      params.append('companyId', options.companyId);
    }

    const queryString = params.toString();
    if (queryString) {
      url = `${url}?${queryString}`;
    }

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to fetch purchase history');
    }

    return await response.json();
  } catch (error: any) {
    toastHandler(error);
    throw error;
  }
};

/**
 * Get agent usage history
 */
export const getAgentUsageHistory = async (
  options?: { agentId?: string; page?: number; limit?: number; companyId?: string }
): Promise<AgentUsageHistoryResponse> => {
  try {
    const { accessToken } = store.getState().auth;

    let url = `${config.BASE_URL}/company-credits/agent-usage-history`;
    const params = new URLSearchParams();

    if (options?.agentId) {
      params.append('agentId', options.agentId);
    }
    if (options?.page) {
      params.append('page', options.page.toString());
    }
    if (options?.limit) {
      params.append('limit', options.limit.toString());
    }
    if (options?.companyId) {
      params.append('companyId', options.companyId);
    }

    const queryString = params.toString();
    if (queryString) {
      url = `${url}?${queryString}`;
    }

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to fetch agent usage history');
    }

    return await response.json();
  } catch (error: any) {
    toastHandler(error);
    throw error;
  }
};

/**
 * Check and update payment status from SkipCash
 */
export const checkPaymentStatus = async (paymentId: string): Promise<{ success: boolean; paymentStatus: string; purchaseLogStatus: string; message: string }> => {
  try {
    const { accessToken } = store.getState().auth;

    const response = await fetch(`${config.BASE_URL}/company-credits/check-payment-status`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({ paymentId }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to check payment status');
    }

    return await response.json();
  } catch (error: any) {
    toastHandler(error);
    throw error;
  }
};

/**
 * Get allocation history
 */
export const getAllocationHistory = async (
  options?: { agentId?: string; actionType?: string; page?: number; limit?: number; companyId?: string }
): Promise<AllocationHistoryResponse> => {
  try {
    const { accessToken } = store.getState().auth;

    let url = `${config.BASE_URL}/company-credits/allocation-history`;
    const params = new URLSearchParams();

    if (options?.agentId) {
      params.append('agentId', options.agentId);
    }
    if (options?.actionType) {
      params.append('actionType', options.actionType);
    }
    if (options?.page) {
      params.append('page', options.page.toString());
    }
    if (options?.limit) {
      params.append('limit', options.limit.toString());
    }
    if (options?.companyId) {
      params.append('companyId', options.companyId);
    }

    const queryString = params.toString();
    if (queryString) {
      url = `${url}?${queryString}`;
    }

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to fetch allocation history');
    }

    return await response.json();
  } catch (error: any) {
    toastHandler(error);
    throw error;
  }
};

export interface ExpirationHistoryItem {
  id: string;
  companyId: string;
  companyCreditId: string;
  expiredCredits: number;
  usedCredits: number | null;
  totalCredits: number | null;
  expiredAt: string;
  processedAt: string;
  packageName?: string;
  packageNameAr?: string;
  packageTotalCredits?: number;
  packageUsedCredits?: number;
  packageAllocatedToAgents?: number;
  packagePurchasedAt?: string;
  packageExpiresAt?: string;
  companyName?: string; // For admin views
  companyEmail?: string; // For admin views
}

export interface ExpirationHistoryResponse {
  data: ExpirationHistoryItem[];
  total: number;
  page: number;
  limit: number;
  summary: {
    totalExpiredCredits: number;
  };
}

/**
 * Get expiration history
 */
export const getExpirationHistory = async (
  options?: { page?: number; limit?: number; companyId?: string }
): Promise<ExpirationHistoryResponse> => {
  try {
    const { accessToken } = store.getState().auth;

    let url = `${config.BASE_URL}/company-credit-expiration-log`;
    const params = new URLSearchParams();

    if (options?.page) {
      params.append('page', options.page.toString());
    }
    if (options?.limit) {
      params.append('limit', options.limit.toString());
    }
    if (options?.companyId) {
      params.append('companyId', options.companyId);
    }

    const queryString = params.toString();
    if (queryString) {
      url = `${url}?${queryString}`;
    }

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to fetch expiration history');
    }

    return await response.json();
  } catch (error: any) {
    toastHandler(error);
    throw error;
  }
};

/**
 * Get free package purchase status
 * Returns list of free package IDs that have been purchased by the company
 */
export const getFreePackageStatus = async (): Promise<{ purchasedPackageIds: string[] }> => {
  try {
    const { accessToken } = store.getState().auth;

    const response = await fetch(`${config.BASE_URL}/free-package-status`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to fetch free package status');
    }

    return await response.json();
  } catch (error: any) {
    toastHandler(error);
    throw error;
  }
};

